import {
  Component,
  OnInit,
  Input,
  forwardRef,
  ElementRef,
  ApplicationRef,
  Injector,
  EmbeddedViewRef,
  ComponentRef,
  createComponent,
  OnDestroy,
  Output,
  EventEmitter,
  HostListener,
  OnChanges,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';

import {
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
  NG_VALIDATORS,
  Validator,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  UntypedFormControl,
} from '@angular/forms';

import dayjs from 'dayjs';
import { WeekPickerPopupComponent } from './week-picker-popup/week-picker-popup.component';
import { NgClass } from '@angular/common';

function getWeekFromString(value: string): number {
  return value ? parseInt(value.split('-')[1].replace('W', ''), 10) : null;
}

function getYearFromString(value: string): number {
  return value ? parseInt(value.split('-')[0], 10) : null;
}

function getDateOfISOWeek(week: number, year: number): Date {
  if (week && year) {
    const date = new Date(year, 0, 1 + (week - 1) * 7);
    const dow = date.getDay();
    const isoWeekStart = date;
    if (dow <= 4) {
      isoWeekStart.setDate(date.getDate() - date.getDay() + 1);
    } else {
      isoWeekStart.setDate(date.getDate() + 8 - date.getDay());
    }
    return isoWeekStart;
  }
  return null;
}

export function minWeekValidator(minWeek: string): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const minDate = dayjs(
      getDateOfISOWeek(getWeekFromString(minWeek), getYearFromString(minWeek)),
    );
    const controlDate = dayjs(
      getDateOfISOWeek(
        getWeekFromString(control.value),
        getYearFromString(control.value),
      ),
    );
    if (minDate.isValid() && controlDate.isValid()) {
      return controlDate.isBefore(minDate)
        ? { minWeek: { value: control.value } }
        : null;
    }
    return null;
  };
}

export function maxWeekValidator(maxWeek: string): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const maxDate = dayjs(
      getDateOfISOWeek(getWeekFromString(maxWeek), getYearFromString(maxWeek)),
    );
    const controlDate = dayjs(
      getDateOfISOWeek(
        getWeekFromString(control.value),
        getYearFromString(control.value),
      ),
    );
    if (maxDate.isValid() && controlDate.isValid()) {
      return controlDate.isAfter(maxDate)
        ? { maxWeek: { value: control.value } }
        : null;
    }
    return null;
  };
}

/**
 * @deprecated Do not add new instances of this component. Use 'ag1-week-range' instead.
 */
@Component({
  selector: 'ag-week-picker',
  templateUrl: './week-picker.component.html',
  styleUrls: ['./week-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => WeekPickerComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => WeekPickerComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgClass],
})
export class WeekPickerComponent
  implements OnInit, ControlValueAccessor, OnDestroy, Validator, OnChanges
{
  private el = inject(ElementRef);

  private appRef = inject(ApplicationRef);
  private injector = inject(Injector);

  constructor() {}

  @Input()
  disabled: boolean;

  @Input()
  isActive = false;

  @Input()
  isInvalid = false;

  @Input()
  showYears = true;

  @Input()
  minWeek: string;

  @Input()
  maxWeek: string;

  @Output()
  onWeekChange = new EventEmitter<boolean>();

  @Input()
  value: string;

  originalValue: string = null;
  week = 0;
  year = 0;

  popup: ComponentRef<WeekPickerPopupComponent>;

  private onChangeCallback: (_: string) => void = (_: string) => {};
  private onTouchedCallback: () => void = () => {};

  @HostListener('document:click', ['$event'])
  clickout(event) {
    if (!this.el.nativeElement.contains(event.target)) {
      this.blur();
    }
  }

  @HostListener('document:focusin', ['$event'])
  externalFocus(event) {
    const doneButton = this.el.nativeElement.querySelector(
      '#week-picker-done-button',
    );

    if (doneButton && event.target === doneButton) {
      this.blur();
    }
  }

  ngOnInit() {
    if (this.value) {
      this.initialWriteValue(this.value);
    } else {
      this.year = new Date().getFullYear();
    }
    window.addEventListener('scroll', this.scroll, true);
  }

  ngOnDestroy() {
    window.removeEventListener('scroll', this.scroll, true);
    this.destroyPopupInstance();
  }

  ngOnChanges(_: SimpleChanges): void {
    this.onChangeCallback(this.value);
  }

  initialWriteValue(value: string): void {
    if (value) {
      if (this.value !== this.originalValue) {
        this.originalValue = value;
      }

      const parts = value.split('-');
      const yearPart = parts[0];
      let weekPart = parts[1];

      if (!this.showYears) {
        weekPart = yearPart;
        this.week = parseInt(weekPart.replace('W', ''), 10);
        this.value = value;
      } else {
        this.year = parseInt(yearPart, 10);
        this.week = parseInt(weekPart.replace('W', ''), 10);
      }
    } else {
      this.week = null;
      this.year = null;
    }
    this.value = value;
  }

  writeValue(value: string): void {
    if (value === null || this.originalValue === value) return;

    if (value) {
      if (value !== this.originalValue) {
        this.onWeekChange.emit(!!this.originalValue);
        this.originalValue = value;
      }

      const parts = value.split('-');
      const yearPart = parts[0];
      let weekPart = parts[1];

      if (!this.showYears) {
        weekPart = yearPart;
        this.week = parseInt(weekPart.replace('W', ''), 10);
        this.value = value;
      } else {
        this.year = parseInt(yearPart, 10);
        this.week = parseInt(weekPart.replace('W', ''), 10);
      }
    } else {
      this.week = null;
      this.year = null;
    }
    this.value = value;
    this.onChangeCallback(this.value);
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(control: UntypedFormControl): ValidationErrors {
    const errors: any = {};

    if (this.minWeek) {
      const minWeekError = minWeekValidator(this.minWeek)(control);
      if (minWeekError) {
        errors.minWeek = minWeekError;
      }
    }

    if (this.maxWeek) {
      const maxWeekError = maxWeekValidator(this.maxWeek)(control);
      if (maxWeekError) {
        errors.maxWeek = maxWeekError;
      }
    }

    if (Object.keys(errors).length > 0) {
      return errors;
    }
    return null;
  }

  toggle(event: Event) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.popup) {
      this.createPopupInstance();
    } else {
      this.destroyPopupInstance();
    }
  }

  blur() {
    this.destroyPopupInstance();
  }

  scroll = (): void => {
    this.positionPopupDomElement(this.getPopupDomElement());
  };

  updateModel() {
    if (this.showYears && this.week && this.year) {
      this.writeValue(`${this.year}-W${this.week.toString().padStart(2, '0')}`);
    } else if (!this.showYears && this.week) {
      this.writeValue(`W${this.week.toString().padStart(2, '0')}`);
    }
  }

  private createPopupInstance() {
    this.popup = createComponent(WeekPickerPopupComponent, {
      environmentInjector: this.appRef.injector,
      elementInjector: this.injector,
    });

    this.popup.instance.week = this.week;
    this.popup.instance.year = this.year;
    this.popup.instance.showYears = this.showYears;

    this.popup.instance.weekChange.subscribe((week) => {
      this.week = week;
      this.updateModel();
    });

    this.popup.instance.yearChange.subscribe((year) => {
      this.year = year;
      this.updateModel();
    });

    this.appRef.attachView(this.popup.hostView);

    const domElem = this.getPopupDomElement();
    this.positionPopupDomElement(domElem);
    document.body.appendChild(domElem);
  }

  private positionPopupDomElement(element: HTMLElement) {
    if (element) {
      const rect = this.el.nativeElement.getBoundingClientRect();
      const alignLeft =
        window.innerWidth - rect.left >= this.popup.instance.width;
      const alignBottom =
        window.innerHeight - rect.top - rect.height >=
        this.popup.instance.height;

      element.style.position = 'fixed';
      element.style.zIndex = '50';

      if (alignLeft) {
        element.style.left = `${rect.left}px`;
      } else {
        element.style.right = `${window.innerWidth - rect.right}px`;
      }

      if (alignBottom) {
        element.style.top = `${rect.top + rect.height}px`;
      } else {
        element.style.top = `${rect.top - this.popup.instance.height}px`;
      }
    }
  }

  private getPopupDomElement() {
    if (!this.popup) {
      return null;
    }
    return (this.popup.hostView as EmbeddedViewRef<any>)
      .rootNodes[0] as HTMLElement;
  }

  private destroyPopupInstance() {
    if (this.popup) {
      this.appRef.detachView(this.popup.hostView);
      this.popup.destroy();
      this.popup = null;
    }
  }
}
